import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import Helmet from "react-helmet";
import { css, cx } from "emotion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faCheck,
  faDownload,
  faFilePdf,
  faSearch,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import Layout from "../../../universal/layout/Layout";

const baseUrl = process.env.REACT_APP_API_URL;
const apiKey = process.env.REACT_APP_LEMOULIN_API_KEY;

// Appel direct (fetch) : l'aperçu est un PDF, que le middleware redux ne sait
// pas lire. Même authentification que le reste de l'app.
const apiFetch = (path, body = null) => {
  const query = new URLSearchParams({ apiKey: apiKey || "" });
  const headers = { "X-Custom-Authorization": `Bearer ${window.localStorage.getItem("token")}` };
  if (body) headers["Content-Type"] = "application/json";
  return fetch(`${baseUrl}${path}?${query.toString()}`, {
    method: body ? "POST" : "GET",
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
};

// Titre et accroche du gabarit, rappelés en placeholder
const DEFAULT_TITLE = "Nos plateaux-repas";
const DEFAULT_SUBTITLE = "Des plateaux-repas frais, faits maison, préparés à Lyon avec soin et engagement.";

// Plateaux par page du PDF, comme côté API (LmvpBuilder::TRAYS_PER_PAGE)
const TRAYS_PER_PAGE = 4;

// Recherche insensible à la casse et aux accents (« vege » trouve « végé »)
const normalize = (text) =>
  (text || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const euros = (centimes) =>
  centimes ? `${(centimes / 100).toFixed(2).replace(".", ",")} € HT` : "";

// Charte : vert de marque, texte foncé, et le marron des sous-titres de l'aperçu
const GREEN = "#429853";
const DARK = "#1d1d1b";
const BROWN = "#997f60";

const s = {
  page: css({ color: DARK, paddingBottom: 32 }),
  header: css({
    margin: "20px 0 16px",
    h1: { fontSize: 22, fontWeight: 700, margin: 0 },
    p: { margin: "4px 0 0", color: "#6c757d", fontSize: 13, maxWidth: 720 },
  }),
  card: css({
    background: "#fff",
    border: "1px solid #e6e3dc",
    borderRadius: 10,
    marginBottom: 16,
    overflow: "hidden",
  }),
  cardHead: css({
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 16px",
    borderBottom: "1px solid #efece6",
    fontWeight: 700,
    fontSize: 14,
  }),
  step: css({
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: GREEN,
    color: "#fff",
    fontSize: 12,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
  cardBody: css({ padding: 16 }),
  counter: css({
    marginLeft: "auto",
    fontSize: 12,
    fontWeight: 600,
    color: GREEN,
    background: "#eaf4ec",
    borderRadius: 999,
    padding: "2px 10px",
  }),
  search: css({
    position: "relative",
    marginBottom: 10,
    svg: { position: "absolute", left: 11, top: 11, color: "#9a9a9a", fontSize: 13 },
    input: { paddingLeft: 32 },
  }),
  list: css({ maxHeight: "42vh", overflowY: "auto", margin: "0 -16px -16px", borderTop: "1px solid #efece6" }),
  row: css({
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "10px 16px",
    borderBottom: "1px solid #f3f1ec",
    cursor: "pointer",
    transition: "background .12s",
    "&:hover": { background: "#faf9f6" },
  }),
  rowSelected: css({ background: "#f1f8f2", "&:hover": { background: "#eaf4ec" } }),
  check: css({
    width: 18,
    height: 18,
    borderRadius: 4,
    border: "1.5px solid #c9c5bc",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 10,
    flexShrink: 0,
    marginTop: 2,
  }),
  checkOn: css({ background: GREEN, borderColor: GREEN }),
  dot: (color, size = 12) =>
    css({
      width: size,
      height: size,
      borderRadius: "50%",
      background: color || "#689d71",
      flexShrink: 0,
      boxShadow: "inset 0 0 0 1px rgba(0,0,0,.08)",
    }),
  rowName: css({ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }),
  price: css({ fontSize: 12, color: BROWN, fontWeight: 600, whiteSpace: "nowrap" }),
  items: css({ fontSize: 12, color: "#7a7a7a", marginTop: 2 }),
  pageLabel: css({
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    color: BROWN,
    margin: "12px 0 6px",
    "&:first-of-type": { marginTop: 0 },
  }),
  selItem: css({
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    border: "1px solid #efece6",
    borderRadius: 8,
    marginBottom: 6,
    background: "#fcfbf9",
  }),
  selName: css({ fontSize: 12, color: "#7a7a7a", marginBottom: 3, lineHeight: 1.2 }),
  iconBtn: css({
    border: "none",
    background: "transparent",
    color: "#8a8a8a",
    width: 26,
    height: 26,
    borderRadius: 6,
    fontSize: 12,
    flexShrink: 0,
    "&:hover:not(:disabled)": { background: "#efece6", color: DARK },
    "&:disabled": { opacity: 0.3 },
  }),
  removeBtn: css({ "&:hover:not(:disabled)": { background: "#fbeaea", color: "#c0392b" } }),
  label: css({ fontSize: 12, fontWeight: 600, marginBottom: 4, color: "#555" }),
  generate: css({
    background: GREEN,
    borderColor: GREEN,
    fontWeight: 600,
    padding: "10px 18px",
    "&:hover, &:focus, &:active": { background: "#37804a !important", borderColor: "#37804a !important" },
  }),
  previewFrame: css({ width: "100%", height: "78vh", border: "none", display: "block", background: "#525659" }),
  empty: css({
    textAlign: "center",
    padding: "80px 24px",
    color: "#8a8a8a",
    svg: { fontSize: 40, color: "#d8d3c8", marginBottom: 14 },
    strong: { display: "block", color: DARK, fontSize: 15, marginBottom: 4 },
  }),
  hint: css({ fontSize: 13, color: "#8a8a8a", margin: 0 }),
};

const sum = {
  tray: css({ marginBottom: 14 }),
  trayName: css({ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 14, marginBottom: 6 }),
  product: css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 12,
    fontSize: 13,
    padding: "5px 0 5px 20px",
    borderBottom: "1px dashed #ece9e2",
  }),
  codes: css({ display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "flex-end", flexShrink: 0 }),
  code: css({
    minWidth: 20,
    height: 20,
    padding: "0 5px",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f4efe8",
    color: BROWN,
  }),
  codeUnknown: css({ background: "#fff4e0", color: "#b7791f" }),
  none: css({ fontSize: 11, color: "#b0b0b0", fontStyle: "italic" }),
  legend: css({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
    gap: "4px 16px",
    marginTop: 10,
    paddingTop: 10,
    borderTop: "1px solid #efece6",
    fontSize: 12,
  }),
  legendItem: css({ display: "flex", alignItems: "center", gap: 6, fontWeight: 600 }),
  unknownNote: css({ fontSize: 12, fontStyle: "italic", color: "#b7791f", marginTop: 8 }),
};

/**
 * Récapitulatif HTML des allergènes, sous l'aperçu : chaque produit avec ses
 * numéros, puis la légende : les allergènes présents sur la page, avec leur
 * numéro réglementaire figé (le même sur toutes les pages). Le récapitulatif
 * suit le découpage du PDF.
 */
const AllergenSummary = ({ pages }) => {
  if (!pages.length) return null;

  return (
    <div className={s.card}>
      <div className={s.cardHead}>Allergènes</div>
      <div className={s.cardBody}>
        {pages.map((page, index) => (
            <div key={index} className={index > 0 ? "mt-4" : undefined}>
              {pages.length > 1 && <div className={s.pageLabel}>Page {index + 1}</div>}
              {page.trays.map((tray, t) => (
                <div key={t} className={sum.tray}>
                  <div className={sum.trayName}>
                    <span className={s.dot(tray.color)} />
                    {tray.name}
                  </div>
                  {tray.products.map((product, p) => (
                    <div key={p} className={sum.product}>
                      <span>{product.name}</span>
                      <span className={sum.codes}>
                        {product.codes ? (
                          product.codes.split(",").map((code) => (
                            <span key={code} className={cx(sum.code, code === "*" && sum.codeUnknown)}>
                              {code}
                            </span>
                          ))
                        ) : (
                          <span className={sum.none}>aucun</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
              {page.legend.length > 0 && (
                <div className={sum.legend}>
                  {page.legend.map((a) => (
                    <span key={a.number} className={sum.legendItem}>
                      <span className={sum.code}>{a.number}</span>
                      {a.label}
                    </span>
                  ))}
                </div>
              )}
              {page.unknown && <div className={sum.unknownNote}>* allergènes communiqués sur demande</div>}
            </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Aperçu de la page « plateaux-repas » du LMVP v2 pour une sélection de
 * plateaux du catalogue : même gabarit et mêmes allergènes que le document,
 * généré à la volée par l'API (rien n'est enregistré).
 */
const LmvpPlateaux = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]); // ids, dans l'ordre de la page
  const [names, setNames] = useState({}); // nom affiché sur la pastille, par id (vide = nom du plateau)
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [subtitle2, setSubtitle2] = useState("");
  const [logo, setLogo] = useState(true); // logo vertical Le Moulin Traiteur en bas à droite, affiché par défaut
  const [generating, setGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [allergenPages, setAllergenPages] = useState([]); // récapitulatif HTML, page par page comme le PDF

  useEffect(() => {
    apiFetch("/api/v2/admin/lmvp/tray-groups.json")
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => setGroups((data.result || data).groups || []))
      .catch(() => toast.error("Impossible de charger les plateaux"))
      .finally(() => setLoading(false));
  }, []);

  // Libère l'aperçu précédent
  useEffect(() => () => pdfUrl && window.URL.revokeObjectURL(pdfUrl), [pdfUrl]);

  const byId = useMemo(() => Object.fromEntries(groups.map((g) => [g.id, g])), [groups]);

  // Chaque mot tapé doit se retrouver quelque part dans le plateau (nom ou
  // produits), dans n'importe quel ordre : « formule 2026 » trouve
  // « Formule Essentiel Végan - printemps-été 2026 ».
  const filtered = useMemo(() => {
    const words = normalize(search).split(/\s+/).filter(Boolean);
    if (!words.length) return groups;
    return groups.filter((g) => {
      const haystack = normalize([g.name, ...g.items].join(" "));
      return words.every((word) => haystack.includes(word));
    });
  }, [groups, search]);

  const toggle = (id) =>
    setSelected((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));

  const move = (index, delta) =>
    setSelected((current) => {
      const next = [...current];
      const target = index + delta;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const generate = async () => {
    setGenerating(true);
    const body = {
      title,
      subtitle,
      subtitle2,
      logo,
      groups: selected.map((id) => ({ id, name: names[id] || "" })),
    };
    try {
      // Le PDF et le récapitulatif HTML des allergènes, calculés de la même façon
      const [res, allergensRes] = await Promise.all([
        apiFetch("/api/v2/admin/lmvp/trays-preview.pdf", body),
        apiFetch("/api/v2/admin/lmvp/trays-allergens.json", body),
      ]);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error((data.result || data).message || "Génération du PDF impossible");
        return;
      }
      setPdfUrl(window.URL.createObjectURL(await res.blob()));
      const allergensData = allergensRes.ok ? await allergensRes.json().catch(() => null) : null;
      setAllergenPages(allergensData ? (allergensData.result || allergensData).pages || [] : []);
    } catch (e) {
      toast.error("Erreur réseau pendant la génération du PDF");
    } finally {
      setGenerating(false);
    }
  };

  const pageCount = Math.ceil(selected.length / TRAYS_PER_PAGE);

  return (
    <Layout>
      <Helmet>
        <title>Aperçu LMVP plateaux-repas</title>
      </Helmet>
      <div className={cx("container-fluid", s.page)}>
        <div className={s.header}>
          <h1>Aperçu LMVP — plateaux-repas</h1>
          <p>
            Composez une page « plateaux-repas » du document « Le Moulin Vous Propose » à partir du catalogue :
            même mise en page, mêmes allergènes (fiches Koust). Généré à la volée, rien n'est enregistré.
          </p>
        </div>

        <Row>
          <Col xl={5} lg={6}>
            {/* 1. Catalogue */}
            <div className={s.card}>
              <div className={s.cardHead}>
                <span className={s.step}>1</span>
                Choisir les plateaux
                {!loading && <span className={s.counter}>{filtered.length} / {groups.length}</span>}
              </div>
              <div className={s.cardBody}>
                <div className={s.search}>
                  <FontAwesomeIcon icon={faSearch} />
                  <Form.Control
                    type="search"
                    placeholder="Rechercher : formule 2026, végé, houmous…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className={s.list}>
                  {loading && (
                    <div className="p-4 text-center text-muted">
                      <Spinner animation="border" size="sm" className="mr-2" /> Chargement des plateaux…
                    </div>
                  )}
                  {!loading && filtered.length === 0 && (
                    <div className="p-4 text-center text-muted small">Aucun plateau ne correspond à la recherche.</div>
                  )}
                  {filtered.map((group) => {
                    const isSelected = selected.includes(group.id);
                    return (
                      <div
                        key={group.id}
                        role="checkbox"
                        aria-checked={isSelected}
                        tabIndex={0}
                        className={cx(s.row, isSelected && s.rowSelected)}
                        onClick={() => toggle(group.id)}
                        onKeyDown={(e) => {
                          if (e.key === " " || e.key === "Enter") {
                            e.preventDefault();
                            toggle(group.id);
                          }
                        }}
                      >
                        <span className={cx(s.check, isSelected && s.checkOn)}>
                          {isSelected && <FontAwesomeIcon icon={faCheck} />}
                        </span>
                        <span className={s.dot(group.color)} style={{ marginTop: 4 }} />
                        <span className="flex-grow-1">
                          <span className="d-flex justify-content-between" style={{ gap: 8 }}>
                            <span className={s.rowName}>{group.name}</span>
                            <span className={s.price}>{euros(group.price)}</span>
                          </span>
                          <div className={s.items}>{group.items.join(" · ")}</div>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* 2. Ordre et noms */}
            <div className={s.card}>
              <div className={s.cardHead}>
                <span className={s.step}>2</span>
                Ordre et noms sur les pastilles
                {selected.length > 0 && (
                  <span className={s.counter}>
                    {selected.length} plateau{selected.length > 1 ? "x" : ""} · {pageCount} page{pageCount > 1 ? "s" : ""}
                  </span>
                )}
              </div>
              <div className={s.cardBody}>
                {selected.length === 0 && <p className={s.hint}>Cochez des plateaux dans le catalogue ci-dessus.</p>}
                {selected.map((id, index) => (
                  <React.Fragment key={id}>
                    {pageCount > 1 && index % TRAYS_PER_PAGE === 0 && (
                      <div className={s.pageLabel}>Page {index / TRAYS_PER_PAGE + 1}</div>
                    )}
                    <div className={s.selItem}>
                      <span className={s.dot(byId[id]?.color, 14)} />
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className={s.selName}>{byId[id]?.name}</div>
                        <Form.Control
                          size="sm"
                          maxLength={60}
                          placeholder="Nom sur la pastille (vide = nom du plateau)"
                          value={names[id] || ""}
                          onChange={(e) => setNames({ ...names, [id]: e.target.value })}
                        />
                      </div>
                      <button type="button" className={s.iconBtn} disabled={index === 0} onClick={() => move(index, -1)} title="Monter">
                        <FontAwesomeIcon icon={faArrowUp} />
                      </button>
                      <button type="button" className={s.iconBtn} disabled={index === selected.length - 1} onClick={() => move(index, 1)} title="Descendre">
                        <FontAwesomeIcon icon={faArrowDown} />
                      </button>
                      <button type="button" className={cx(s.iconBtn, s.removeBtn)} onClick={() => toggle(id)} title="Retirer">
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* 3. Mise en page */}
            <div className={s.card}>
              <div className={s.cardHead}>
                <span className={s.step}>3</span>
                Mise en page
              </div>
              <div className={s.cardBody}>
                <Form.Group className="mb-3">
                  <Form.Label className={s.label}>Titre</Form.Label>
                  <Form.Control maxLength={80} placeholder={DEFAULT_TITLE} value={title} onChange={(e) => setTitle(e.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className={s.label}>
                    Sous-titre <span style={{ color: BROWN }} title="Affiché en marron sur le PDF">●</span>
                  </Form.Label>
                  <Form.Control maxLength={160} placeholder={DEFAULT_SUBTITLE} value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className={s.label}>Sous-titre supplémentaire</Form.Label>
                  <Form.Control maxLength={160} placeholder="Facultatif" value={subtitle2} onChange={(e) => setSubtitle2(e.target.value)} />
                </Form.Group>
                <Form.Check
                  type="switch"
                  id="lmvp-logo"
                  label="Logo Le Moulin Traiteur en bas à droite"
                  checked={logo}
                  onChange={(e) => setLogo(e.target.checked)}
                />
              </div>
            </div>

            <div className="d-flex align-items-center" style={{ gap: 10 }}>
              <Button className={s.generate} disabled={!selected.length || generating} onClick={generate}>
                {generating ? (
                  <><Spinner animation="border" size="sm" className="mr-2" />Génération…</>
                ) : (
                  <><FontAwesomeIcon icon={faFilePdf} className="mr-2" />{pdfUrl ? "Régénérer le PDF" : "Générer le PDF"}</>
                )}
              </Button>
              {!selected.length && <span className={s.hint}>Choisissez au moins un plateau.</span>}
            </div>
          </Col>

          <Col xl={7} lg={6} className="mt-3 mt-lg-0">
            <div className={s.card}>
              <div className={s.cardHead}>
                Aperçu
                {pdfUrl && (
                  <Button variant="outline-secondary" size="sm" className="ml-auto" href={pdfUrl} download="plateaux-repas.pdf">
                    <FontAwesomeIcon icon={faDownload} className="mr-1" /> Télécharger
                  </Button>
                )}
              </div>
              {pdfUrl ? (
                <iframe title="Aperçu PDF" src={pdfUrl} className={s.previewFrame} />
              ) : (
                <div className={s.empty}>
                  <FontAwesomeIcon icon={faFilePdf} />
                  <strong>Aucun aperçu pour l'instant</strong>
                  Choisissez des plateaux, ajustez la mise en page puis générez le PDF.
                </div>
              )}
            </div>
            {pdfUrl && <AllergenSummary pages={allergenPages} />}
          </Col>
        </Row>
      </div>
    </Layout>
  );
};

export default LmvpPlateaux;
